import { useMemo, useState } from "react";
import { GetListResponse, useGo, useModal, useTranslate } from "@refinedev/core";
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { CompanyFormSchema } from "@/utils/zod/companyFormValidation";
import { Stack, Button } from "@mui/material";
import { DeleteButton, ShowButton } from "@refinedev/mui";
import { EditButton } from "@refinedev/mui";
import { SendOutstandingModal } from "../SendOutstandingModal";
import { useTableState } from "@/hooks/useTableState";

type Props = {
  tableQuery: {
    data?: GetListResponse<CompanyFormSchema>;
    isLoading: boolean;
    isError: boolean;
  };
};

export const CompanyListTable = ({ tableQuery }: Props) => {
  const { data, isLoading, isError } = tableQuery;
  const t = useTranslate();
  const go = useGo();
  const { visible, show, close } = useModal();
  const { tableState, onColumnFiltersChange, onGlobalFilterChange, onSortingChange } = useTableState<CompanyFormSchema>({
    initialState: {
      columnPinning: { left: ['mrt-row-select', 'ledger_name'], right: ['actions'] }
    }
  });

  // Add state to track selected rows
  const [rowSelection, setRowSelection] = useState({});
  const enhancedRowSelection = Object.keys(rowSelection).map((key) => {
    const row = data?.data.find((row) => row.ledger_name === key);
    return {
      ledger_name: row?.ledger_name ?? "",
      members: row?.accounts?.map((account) => ({
        id: account.id,
        name: account.name,
        phone_number: account.user?.phone_number,
      })),
      send_outstanding_to_accounts: row?.send_outstanding_to_accounts?.map((account) => {
        if (typeof account === "string") {
          return account;
        } else {
          return {
            id: account.id,
            name: account.name,
            phone_number: account.user?.phone_number,
          };
        }
      }),
    };
  });
  

  const columns = useMemo<MRT_ColumnDef<CompanyFormSchema>[]>(
    () => [
      {
        accessorKey: "ledger_name",
        header: t("company.fields.ledger_name"),
        size: 200,
      },
      {
        accessorKey: "legal_name",
        header: t("company.fields.legal_name"),
        size: 200,
      },
      {
        accessorKey: "email",
        header: t("company.fields.email"),
        size: 160,
      },
      {
        accessorKey: "gstin",
        header: t("company.fields.gstin"),
        size: 160,
      },
      {
        accessorKey: "pan_number",
        header: t("company.fields.pan_number"),
        size: 160,
      },
      {
        accessorKey: "notes",
        header: t("company.fields.notes"),
        size: 160,
      },
      {
        accessorFn: (row) => row.addresses?.[0]?.branch_name,
        header: 'Address 1 Branch Name',
        size: 160,
      },
      {
        accessorFn: (row) => row.addresses?.[0]?.line1,
        header: 'Address 1 Address Line',
        size: 160,
      },
      {
        accessorFn: (row) => row.addresses?.[0]?.city,
        header: 'Address 1 City',
        size: 160,
      },
      {
        accessorFn: (row) => row.addresses?.[0]?.country,
        header: 'Address 1 Country',
        size: 160,
      },
      {
        accessorFn: (row) => row.addresses?.[0]?.gstin,
        header: 'Address 1 GSTIN',
        size: 160,
      },
      {
        accessorFn: (row) => row.addresses?.[0]?.pincode,
        header: 'Address 1 Pincode',
        size: 160,
      },
      {
        accessorFn: (row) => row.addresses?.[0]?.state,
        header: 'Address 1 State',
        size: 160,
      },
      {
        accessorFn: (row) => row.addresses?.[0]?.transport_name,
        header: 'Address 1 Transport Name',
        size: 160,
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        size: 160,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: function render({ row }) {
          return (
            <Stack direction="row" spacing={1}>
              <EditButton hideText resource="companies" recordItemId={row.original.ledger_name} />
              <ShowButton hideText recordItemId={row.original.ledger_name} />
              <DeleteButton 
                hideText 
                recordItemId={row.original.ledger_name} 
                errorNotification={(error: any) => {
                  return {
                    message: "Error deleting record",
                    description: error?.response.data.error.includes("orders_customer_ledger_name_fkey") ? "This company is associated with orders. You can not delete the company." : error?.response.data.error.includes("accounts_company_ledger_name_fkey") ? "This company is associated with accounts. Please remove the accounts first." : "An error occurred while deleting",
                    type: "error"
                  };
                }}
              />
            </Stack>
          );
        },
      },
    ],
    [t, go],
  );

  return (
    <>
      <MaterialReactTable
        columns={columns}
        data={data?.data ?? []}
        enableTopToolbar
        enableBottomToolbar
        enableColumnFilters
        enableGlobalFilter
        enablePagination
        enableRowSelection
        muiTableBodyRowProps={{ hover: true }}
        muiPaginationProps={{
          rowsPerPageOptions: [10, 20, 50, 100],
          showFirstButton: true,
          showLastButton: true,
        }}
        state={{
          ...tableState,
          isLoading,
          rowSelection,
        }}
        onColumnFiltersChange={onColumnFiltersChange}
        onGlobalFilterChange={onGlobalFilterChange}
        onSortingChange={onSortingChange}
        onRowSelectionChange={setRowSelection}
        muiToolbarAlertBannerProps={
          isError
            ? {
              color: 'error',
              children: 'Error loading data',
            }
            : undefined
        }
        enableColumnResizing
        enableColumnPinning
        enableFullScreenToggle
        enableDensityToggle
        enableGlobalFilterModes
        positionGlobalFilter="right"
        getRowId={(row) => row.ledger_name ?? ''}
        renderBottomToolbarCustomActions={() => {
          // Only show the button if there are selected rows
          const selectedRowCount = Object.keys(rowSelection).length;

          return selectedRowCount > 0 ? (
            <Button
              variant="contained"
              color="primary"
              onClick={show}
              sx={{ ml: 2 }}
            >
              Send Outstanding ({selectedRowCount})
            </Button>
          ) : <div></div>;
        }}
      />
      <SendOutstandingModal enhancedRowSelection={enhancedRowSelection} setRowSelection={setRowSelection} visible={visible} close={close}/>
    </>
  );
};
